import { useTranslation } from "react-i18next";
import styled from "styled-components";

import ContainerHeader from "../components/containerHeader";
import { Divider } from "primereact/divider";
import { Accordion, AccordionTab } from "primereact/accordion";
import React from "react";
import { Locale } from "../types/ui-types";

const ppwrReportingInstructionsLinks: Record<Locale, string> = {
  [Locale.FI]: "https://sumi.fi/ppwr-raportointiohjeet",
  [Locale.EN]: "https://sumi.fi/en/ppwr-reporting-instructions",
  [Locale.SWE]: "https://sumi.fi/sv/ppwr-rapporteringsanvisningar",
};

const reportingInstructionsLinks: Record<Locale, string> = {
  [Locale.FI]: "https://sumi.fi/raportointi",
  [Locale.EN]: "https://sumi.fi/en/reporting",
  [Locale.SWE]: "https://sumi.fi/sv/rapportering",
};

const eudocLinks: Record<Locale, string> = {
  [Locale.FI]:
    "https://sumi.fi/ppwr-kielletyt-aineet-ja-vaatimustenmukaisuusvakuutus",
  [Locale.EN]:
    "https://sumi.fi/en/ppwr-restricted-substances-and-declaration-of-conformity",
  [Locale.SWE]:
    "https://sumi.fi/sv/ppwr-f%C3%B6rbjudna-%C3%A4mnen-och-f%C3%B6rs%C3%A4kran-om-%C3%B6verensst%C3%A4mmelse",
};

const webinarLinks: Record<Locale, string> = {
  [Locale.FI]: "https://sumi.fi/sumion-webinaari-ja-raportointipajat",
  [Locale.EN]: "https://sumi.fi/en/sumion-webinar-reporting-workshop",
  [Locale.SWE]:
    "https://sumi.fi/sv/sumion-webbinariet-och-rapporteringsworkshop",
};

const palletInstructionsLinks: Record<Locale, string> = {
  [Locale.FI]: "https://sumi.fi/raportointi/kuormalavat",
  [Locale.EN]: "https://sumi.fi/en/reporting/pallets",
  [Locale.SWE]: "https://sumi.fi/sv/rapportering/lastpallar",
};

const consumerBusinessInstructionsLinks: Record<Locale, string> = {
  [Locale.FI]: "https://sumi.fi/raportointi/kuluttaja-ja-yrityspakkaukset",
  [Locale.EN]: "https://sumi.fi/en/reporting/consumer-and-business-packaging",
  [Locale.SWE]:
    "https://sumi.fi/sv/rapportering/konsument-och-f%C3%B6retagsf%C3%B6rpackningar",
};

const reportingAndBillingScheduleLinks: Record<Locale, string> = {
  [Locale.FI]:
    "https://sumi.fi/raportointi/raportoinnin-ja-laskutuksen-aikataulu",
  [Locale.EN]: "https://sumi.fi/en/reporting-and-billing-schedule",
  [Locale.SWE]:
    "https://sumi.fi/sv/tidtabell-f%C3%B6r-rapportering-och-fakturering",
};

const supListingLinks: Record<Locale, string> = {
  [Locale.FI]:
    "https://sumi.fi/hubfs/SUP-listaus-Sumi-11.11.2025.pdf?hsCtaAttrib=262984515801&hsLang=fi",
  [Locale.EN]:
    "https://sumi.fi/hubfs/SUP-listing-Sumi-11.11.2025-1.pdf?hsCtaAttrib=270094774464&hsLang=en",
  [Locale.SWE]:
    "https://sumi.fi/hubfs/SUP-listing-Sumi-11.11.2025-1.pdf?hsCtaAttrib=270094774464&hsLang=en",
};

const PaddedContainer = styled.div`
  padding: 4rem 4rem;
  text-align: left;
`;

const Title = styled.h1`
  margin: 0;
  margin-bottom: 2rem;
  font-size: 24px;
  font-weight: 500;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 1rem;

  @media (max-width: 1028px) {
    flex-direction: column;
    padding-bottom: 2rem;
    gap: 2rem;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  gap: 1rem;
`;

const Heading = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 500;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
`;

const StyledLink = styled.a`
  color: var(--sumi-blue) !important;
  text-decoration: underline !important;
  cursor: pointer;
`;

const AnnouncementTitle = styled.b`
  font-weight: 600;
`;

const SubHeading = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const FAQAccordionWrapper = styled.div`
  padding: 0;
  padding-top: 0.5rem;

  .p-accordion-header-link {
    padding: 0.8rem !important;
    background-color: transparent !important;
    color: var(--sumi-blue) !important;
    font-weight: 600 !important;

    .p-accordion-toggle-icon {
      width: 12px;
    }
  }

  .p-accordion-content {
    padding: 0.5rem !important;
    padding-left: 2rem !important;
    background-color: var(--background-light) !important;
    color: var(--text-primary) !important;
    border: none !important;

    > p {
      margin: 0;
    }
  }
`;

const FAQGroupTitle = styled.h3`
  margin: 1rem 0rem 0.5rem 0rem;
  font-size: 16px;
  font-weight: 600;
`;

const Home: React.FC = () => {
  const { t, i18n } = useTranslation();

  const faqsLeft = [
    {
      key: 1,
      group: t("front-page.faq.question-group-1"),
      items: [
        {
          question: t("front-page.faq.items.question-1.question"),
          answer: (
            <>
              <p>{t("front-page.faq.items.question-1.answer-part-1")}</p>
              <br />
              <p>{t("front-page.faq.items.question-1.answer-part-2")}</p>
              <ul>
                <li>
                  {t("front-page.faq.items.question-1.answer-list-item-1")}
                </li>
                <li>
                  {t("front-page.faq.items.question-1.answer-list-item-2")}
                </li>
                <li>
                  {t("front-page.faq.items.question-1.answer-list-item-3")}
                </li>
              </ul>
              <p>{t("front-page.faq.items.question-1.answer-part-3")}</p>
              <br />
              <p>
                {t("front-page.faq.items.question-1.answer-part-4")}{" "}
                <StyledLink
                  tabIndex={0}
                  href={
                    reportingAndBillingScheduleLinks[i18n.language as Locale]
                  }
                  target="_blank"
                >
                  {t("front-page.label-link")}
                </StyledLink>
              </p>
            </>
          ),
        },
        {
          question: t("front-page.faq.items.question-2.question"),
          answer: (
            <>
              <p>{t("front-page.faq.items.question-2.answer-part-1")}</p>
              <br />
              <p>{t("front-page.faq.items.question-2.answer-part-2")}</p>
              <ul>
                <li>
                  {t("front-page.faq.items.question-2.answer-list-item-1")}
                </li>
                <li>
                  {t("front-page.faq.items.question-2.answer-list-item-2")}
                </li>
                <li>
                  {t("front-page.faq.items.question-2.answer-list-item-3")}
                </li>
                <li>
                  {t("front-page.faq.items.question-2.answer-list-item-4")}
                </li>
              </ul>
              <p>{t("front-page.faq.items.question-2.answer-part-3")}</p>
            </>
          ),
        },
        {
          question: t("front-page.faq.items.question-3.question"),
          answer: t("front-page.faq.items.question-3.answer"),
        },
      ],
    },
    {
      key: 2,
      group: t("front-page.faq.question-group-2"),
      items: [
        {
          question: t("front-page.faq.items.question-4.question"),
          answer: (
            <>
              <p>{t("front-page.faq.items.question-4.answer-part-1")}</p>
              <br />
              <p>{t("front-page.faq.items.question-4.answer-part-2")}</p>
              <br />
              <p>{t("front-page.faq.items.question-4.answer-part-3")}</p>
            </>
          ),
        },
        {
          question: t("front-page.faq.items.question-5.question"),
          answer: t("front-page.faq.items.question-5.answer"),
        },
        {
          question: t("front-page.faq.items.question-6.question"),
          answer: (
            <>
              <p>{t("front-page.faq.items.question-6.answer-part-1")}</p>
              <br />
              <p>{t("front-page.faq.items.question-6.answer-part-2")}</p>
              <br />
              <p>{t("front-page.faq.items.question-6.answer-part-3")}</p>
            </>
          ),
        },
      ],
    },
    {
      key: 3,
      group: t("front-page.faq.question-group-3"),
      items: [
        {
          question: t("front-page.faq.items.question-7.question"),
          answer: (
            <>
              <p>{t("front-page.faq.items.question-7.answer-part-1")}</p>
              <br />
              <p>{t("front-page.faq.items.question-7.answer-part-2")}</p>
            </>
          ),
        },
      ],
    },
  ];

  const faqsRight = [
    {
      key: 1,
      group: t("front-page.faq.question-group-4"),
      items: [
        {
          question: t("front-page.faq.items.question-8.question"),
          answer: (
            <>
              <p>{t("front-page.faq.items.question-8.answer-part-1")}</p>
              <br />
              <p>{t("front-page.faq.items.question-8.answer-part-2")}</p>
              <br />
              <p>
                {t("front-page.faq.items.question-8.answer-part-3")}{" "}
                <StyledLink
                  tabIndex={0}
                  href={supListingLinks[i18n.language as Locale]}
                  target="_blank"
                >
                  {t("front-page.label-link")}
                </StyledLink>
              </p>
            </>
          ),
        },
        {
          question: t("front-page.faq.items.question-9.question"),
          answer: (
            <>
              <p>{t("front-page.faq.items.question-9.answer-part-1")}</p>
              <br />
              <p>{t("front-page.faq.items.question-9.answer-part-2")}</p>
              <br />
              <p>{t("front-page.faq.items.question-9.answer-part-3")}</p>
              <br />
              <p>{t("front-page.faq.items.question-9.answer-part-4")}</p>
              <br />
              <p>{t("front-page.faq.items.question-9.answer-part-5")}</p>
            </>
          ),
        },
      ],
    },
    {
      key: 2,
      group: t("front-page.faq.question-group-5"),
      items: [
        {
          question: t("front-page.faq.items.question-10.question"),
          answer: t("front-page.faq.items.question-10.answer"),
        },
      ],
    },
    {
      key: 3,
      group: t("front-page.faq.question-group-6"),
      items: [
        {
          question: t("front-page.faq.items.question-11.question"),
          answer: (
            <>
              <p>{t("front-page.faq.items.question-11.answer-part-1")}</p>
              <br />
              <p>{t("front-page.faq.items.question-11.answer-part-2")}</p>
            </>
          ),
        },
        {
          question: t("front-page.faq.items.question-12.question"),
          answer: t("front-page.faq.items.question-12.answer"),
        },
        {
          question: t("front-page.faq.items.question-13.question"),
          answer: t("front-page.faq.items.question-13.answer"),
        },
      ],
    },
  ];

  return (
    <>
      <ContainerHeader
        title={t("header-front-page")}
        description={t("text-front-page-description")}
      />
      <PaddedContainer className="padded-container">
        <Title>{t("header-front-page")}</Title>
        <Row>
          <Column>
            <Section>
              <Heading>{t("front-page.title-help-desk")}</Heading>
              <br />
              <span style={{ marginRight: "10px" }}>
                {t("front-page.text-help-desk")}
              </span>

              <span>
                {t("front-page.text-help-desk-contact-info-part-1")}{" "}
                <b>030 635 8100</b>{" "}
                {t("front-page.text-help-desk-contact-info-part-2")}
              </span>
            </Section>
          </Column>
          <Column>
            <Stack>
              <Heading>{t("front-page.title-announcements")}</Heading>
              <Section>
                <AnnouncementTitle>
                  {t("front-page.announcement-eudoc-title")}
                </AnnouncementTitle>
                <span>{t("front-page.announcement-eudoc-text")}</span>
                <span>
                  {t("front-page.announcement-eudoc-link-text")}{" "}
                  <StyledLink
                    tabIndex={0}
                    href={eudocLinks[i18n.language as Locale]}
                    target="_blank"
                  >
                    {t("front-page.label-link")}
                  </StyledLink>
                </span>
              </Section>
              <Section>
                <AnnouncementTitle>
                  {t("front-page.announcement-ppwr-title")}
                </AnnouncementTitle>
                <span>{t("front-page.announcement-ppwr-text")}</span>
                <span>
                  {t("front-page.announcement-ppwr-link-text")}{" "}
                  <StyledLink
                    tabIndex={0}
                    href={
                      ppwrReportingInstructionsLinks[i18n.language as Locale]
                    }
                    target="_blank"
                  >
                    {t("front-page.label-link")}
                  </StyledLink>
                </span>
              </Section>
            </Stack>
          </Column>
        </Row>
        <br />
        <Divider />
        <br />
        <Heading>{t("front-page.title-reporting-instructions")}</Heading>
        <br />
        <Stack>
          <Section>
            <SubHeading>{t("front-page.title-instructions")}</SubHeading>
            <span>
              {t("front-page.text-instructions")}{" "}
              <StyledLink
                tabIndex={0}
                href={reportingInstructionsLinks[i18n.language as Locale]}
                target="_blank"
              >
                {t("front-page.label-link")}
              </StyledLink>
            </span>
          </Section>
          <Section>
            <SubHeading>{t("front-page.title-webinar")}</SubHeading>
            <span>
              {t("front-page.text-link-to-latest-webinar")}{" "}
              <StyledLink
                tabIndex={0}
                href={webinarLinks[i18n.language as Locale]}
                target="_blank"
              >
                {t("front-page.label-link")}
              </StyledLink>
            </span>
          </Section>
          <Section>
            <SubHeading>
              {t("front-page.title-additional-instructions")}
            </SubHeading>
            <span>
              {t("front-page.text-pallet-instructions")}{" "}
              <StyledLink
                tabIndex={0}
                href={palletInstructionsLinks[i18n.language as Locale]}
                target="_blank"
              >
                {t("front-page.label-link")}
              </StyledLink>
            </span>
            <span>
              {t("front-page.text-consumer-business-instructions")}{" "}
              <StyledLink
                tabIndex={0}
                href={
                  consumerBusinessInstructionsLinks[i18n.language as Locale]
                }
                target="_blank"
              >
                {t("front-page.label-link")}
              </StyledLink>
            </span>
          </Section>
        </Stack>
        <br />
        <Divider />
        <br />
        <Heading>{t("front-page.faq.title")}</Heading>
        <Row>
          <Column>
            <FAQAccordionWrapper>
              {faqsLeft.map((faqGroup) => (
                <React.Fragment key={faqGroup.key}>
                  <FAQGroupTitle>{faqGroup.group}</FAQGroupTitle>
                  <Accordion multiple activeIndex={[]}>
                    {faqGroup.items.map((item, index) => (
                      <AccordionTab key={index} header={item.question}>
                        {item.answer}
                      </AccordionTab>
                    ))}
                  </Accordion>
                </React.Fragment>
              ))}
            </FAQAccordionWrapper>
          </Column>
          <Column>
            <FAQAccordionWrapper>
              {faqsRight.map((faqGroup) => (
                <React.Fragment key={faqGroup.key}>
                  <FAQGroupTitle>{faqGroup.group}</FAQGroupTitle>
                  <Accordion multiple activeIndex={[]}>
                    {faqGroup.items.map((item, index) => (
                      <AccordionTab key={index} header={item.question}>
                        {item.answer}
                      </AccordionTab>
                    ))}
                  </Accordion>
                </React.Fragment>
              ))}
            </FAQAccordionWrapper>
          </Column>
        </Row>
        <br />
        <Divider />
        <br />
        <Section>
          <Heading>{t("front-page.title-privacy-policy")}</Heading>
          <span>{t("front-page.text-privacy-policy")}</span>
          <StyledLink tabIndex={0}>
            {t("front-page.label-privacy-policy-link")}
          </StyledLink>
        </Section>
      </PaddedContainer>
    </>
  );
};

export default Home;
